import { clsx } from 'clsx';

const skeletonBase = 'motion-loading-pulse rounded-md bg-accent-muted';

interface SkeletonBaseProps {
  count?: number;
  className?: string;
  tableColumns?: number;
}

// ------------------------------------------------------------------
// SkeletonTableRow — status dot + title + 3-4 cells with varied widths
// ------------------------------------------------------------------
export function SkeletonTableRow({ count = 1, className, tableColumns }: SkeletonBaseProps) {
  const rows = Array.from({ length: count }).map((_, i) => (
    <div
      key={i}
      style={{ animationDelay: `${i * 60}ms` }}
      className={clsx(
        'flex items-center gap-4 px-4 py-3 border-b border-[rgb(var(--color-border-primary)/0.4)]',
        className
      )}
    >
      {/* Status dot */}
      <div className={clsx(skeletonBase, 'h-2.5 w-2.5 rounded-full shrink-0')} />
      {/* Title */}
      <div className={clsx(skeletonBase, 'h-4 flex-1 max-w-[200px]')} />
      {/* Cells */}
      <div className={clsx(skeletonBase, 'h-4 w-20 hidden sm:block')} />
      <div className={clsx(skeletonBase, 'h-4 w-28 hidden md:block')} />
      <div className={clsx(skeletonBase, 'h-4 w-16 hidden lg:block')} />
      <div className={clsx(skeletonBase, 'h-6 w-16 rounded-full')} />
    </div>
  ));

  if (tableColumns) {
    return (
      <tr>
        <td colSpan={tableColumns} className="p-0">
          <div role="status" aria-label="Loading" aria-live="polite">
            {rows}
          </div>
        </td>
      </tr>
    );
  }

  return (
    <div role="status" aria-label="Loading" aria-live="polite">
      {rows}
    </div>
  );
}

// ------------------------------------------------------------------
// SkeletonStatCard — large number + label + trend indicator
// ------------------------------------------------------------------
export function SkeletonStatCard({ count = 1, className }: SkeletonBaseProps) {
  const cards = Array.from({ length: count });

  const inner = (i: number) => (
    <div
      key={i}
      style={{ animationDelay: `${i * 60}ms` }}
      className={clsx(
        'rounded-xl border border-[rgb(var(--color-border-primary)/0.5)] bg-[rgb(var(--color-bg-secondary))] p-5',
        className
      )}
    >
      {/* Large number */}
      <div className={clsx(skeletonBase, 'h-8 w-24 mb-2')} />
      {/* Label */}
      <div className={clsx(skeletonBase, 'h-3.5 w-32 mb-3')} />
      {/* Trend */}
      <div className="flex items-center gap-2">
        <div className={clsx(skeletonBase, 'h-3 w-3 rounded-full')} />
        <div className={clsx(skeletonBase, 'h-3 w-16')} />
      </div>
    </div>
  );

  if (count > 1) {
    return (
      <div
        className={clsx(
          'grid gap-4',
          count === 2 ? 'grid-cols-2' : count === 3 ? 'grid-cols-3' : 'grid-cols-2 lg:grid-cols-4'
        )}
      >
        {cards.map((_, i) => inner(i))}
      </div>
    );
  }

  return inner(0);
}

// ------------------------------------------------------------------
// SkeletonChartArea — axes + bar ghost outlines
// ------------------------------------------------------------------
export function SkeletonChartArea({ count = 1, className }: SkeletonBaseProps) {
  const barHeights = [45, 65, 35, 80, 55, 70, 40, 60, 75, 50];

  return (
    <div role="status" aria-label="Loading" aria-live="polite">
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          style={{ animationDelay: `${i * 60}ms` }}
          className={clsx(
            'rounded-xl border border-[rgb(var(--color-border-primary)/0.5)] bg-[rgb(var(--color-bg-secondary))] p-5',
            className
          )}
        >
          {/* Chart title skeleton */}
          <div className={clsx(skeletonBase, 'h-4 w-40 mb-4')} />
          {/* Chart area */}
          <div className="relative h-40 flex items-end gap-1.5 pl-8 pb-6">
            {/* Y-axis ghost lines */}
            <div className="absolute left-0 top-0 bottom-6 flex flex-col justify-between">
              {[0, 1, 2, 3].map((j) => (
                <div key={j} className={clsx(skeletonBase, 'h-3 w-6')} />
              ))}
            </div>
            {/* Bars */}
            {barHeights.map((h, j) => (
              <div
                key={j}
                className={clsx(skeletonBase, 'flex-1 rounded-t-sm')}
                style={{ height: `${h}%` }}
              />
            ))}
          </div>
          {/* X-axis labels */}
          <div className="flex gap-1.5 pl-8 mt-1">
            {barHeights.map((_, j) => (
              <div key={j} className={clsx(skeletonBase, 'flex-1 h-2.5')} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ------------------------------------------------------------------
// SkeletonFormSection — label + input field shapes
// ------------------------------------------------------------------
export function SkeletonFormSection({ count = 1, className }: SkeletonBaseProps) {
  return (
    <div className={clsx('space-y-5', className)}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} style={{ animationDelay: `${i * 60}ms` }} className="space-y-1.5">
          {/* Label */}
          <div className={clsx(skeletonBase, 'h-3.5 w-24')} />
          {/* Input */}
          <div className={clsx(skeletonBase, 'h-10 w-full rounded-lg')} />
        </div>
      ))}
    </div>
  );
}

// ------------------------------------------------------------------
// SkeletonDetailHeader — back button + title + status badge + actions
// ------------------------------------------------------------------
export function SkeletonDetailHeader({ count = 1, className }: SkeletonBaseProps) {
  return (
    <div role="status" aria-label="Loading" aria-live="polite">
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          style={{ animationDelay: `${i * 60}ms` }}
          className={clsx(
            'flex items-center gap-4 px-4 py-4 border-b border-[rgb(var(--color-border-primary)/0.5)]',
            className
          )}
        >
          {/* Back button */}
          <div className={clsx(skeletonBase, 'h-8 w-8 rounded-lg shrink-0')} />
          {/* Title */}
          <div className={clsx(skeletonBase, 'h-6 w-48')} />
          {/* Status badge */}
          <div className={clsx(skeletonBase, 'h-5 w-20 rounded-full')} />
          {/* Spacer */}
          <div className="flex-1" />
          {/* Action buttons */}
          <div className={clsx(skeletonBase, 'h-8 w-20 rounded-lg')} />
          <div className={clsx(skeletonBase, 'h-8 w-8 rounded-lg')} />
        </div>
      ))}
    </div>
  );
}
